import { useState, useEffect } from 'react';
import { Bell, X, FileText, ParkingCircle, Calendar, Star, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

/**
 * ReminderToast - In-app toast notification for reminders
 * Shows when a reminder is triggered
 */
const ReminderToast = ({ reminder, onDismiss, onAction }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(reminder.id), 300);
  };

  const getIcon = () => {
    switch (reminder.type) {
      case 'document':
        return <FileText className="h-5 w-5 text-slate-600" />;
      case 'parking':
        return <ParkingCircle className="h-5 w-5 text-purple-600" />;
      case 'calendar':
        return <Calendar className="h-5 w-5 text-blue-600" />;
      case 'date':
        return <Star className="h-5 w-5 text-amber-600" />;
      default:
        return <Bell className="h-5 w-5 text-emerald-600" />;
    }
  };

  const getColor = () => {
    switch (reminder.type) {
      case 'document':
        return 'border-l-slate-500';
      case 'parking':
        return 'border-l-purple-500';
      case 'calendar':
        return 'border-l-blue-500';
      case 'date':
        return 'border-l-amber-500';
      default:
        return 'border-l-emerald-500';
    }
  };

  return (
    <div
      className={cn(
        "bg-white rounded-lg shadow-lg border border-slate-200 border-l-4 p-4",
        "transform transition-all duration-300",
        isExiting ? "opacity-0 translate-x-full" : "opacity-100 translate-x-0",
        getColor()
      )}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900">{reminder.title}</p>
          <p className="text-sm text-slate-500 mt-0.5">{reminder.message}</p>
        </div>
        <div className="flex items-center gap-1">
          {onAction && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => {
                onAction(reminder);
                handleDismiss();
              }}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * ReminderNotificationCenter - Container for in-app reminder notifications
 */
const ReminderNotificationCenter = () => {
  const [reminders, setReminders] = useState([]);
  const navigate = useNavigate();

  // Listen for reminder events
  useEffect(() => {
    const handleReminder = (event) => {
      const reminder = event.detail;
      setReminders(prev => {
        // Prevent duplicates
        if (prev.some(r => r.id === reminder.id)) return prev;
        return [...prev, reminder].slice(-5); // Keep max 5
      });

      // Auto-dismiss after 10 seconds
      setTimeout(() => {
        setReminders(prev => prev.filter(r => r.id !== reminder.id));
      }, 10000);
    };

    window.addEventListener('app-reminder', handleReminder);
    return () => window.removeEventListener('app-reminder', handleReminder);
  }, []);

  const handleDismiss = (id) => {
    setReminders(prev => prev.filter(r => r.id !== id));
  };

  const handleAction = (reminder) => {
    switch (reminder.type) {
      case 'document':
        navigate(`/documents/${reminder.itemId}`);
        break;
      case 'parking':
        navigate('/parking');
        break;
      case 'calendar':
        navigate('/calendar');
        break;
      case 'date':
        navigate('/dates');
        break;
      default:
        break;
    }
  };

  if (reminders.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-50 space-y-2 max-w-sm w-full"
      data-testid="reminder-notification-center"
    >
      {reminders.map(reminder => (
        <ReminderToast
          key={reminder.id}
          reminder={reminder}
          onDismiss={handleDismiss}
          onAction={handleAction}
        />
      ))}
    </div>
  );
};

/**
 * Dispatch a reminder notification
 */
export const dispatchReminder = (reminder) => {
  window.dispatchEvent(new CustomEvent('app-reminder', { detail: reminder }));
};

export default ReminderNotificationCenter;
